import { EditOutlined } from '@ant-design/icons';
import { IssueSourceType } from '@gooduncles/gu-app-schema';
import { ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'antd';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';

import { loadGridState } from 'src/utils/ag-grid-util';
import {
  createCategoryWithTaskCountRenderer,
  createCreatorCellRenderer,
  createImagesCellRenderer,
  createIssueLiabilitiesCellRenderer,
  createProductsCellRenderer,
  createStatusCellRenderer,
  createStatusDateSummaryCellRenderer,
} from 'src/utils/issue/issue-ag-grid-renderers';
import {
  categoryCellStyle,
  categoryFilterValueFormatter,
  createAmountColumnGroup,
  handleIssuedByCellValueChanged,
  issueCaseStatusValueFormatter,
  issuedByCellEditorParams,
  issuedByValueFormatter,
  productsFilterParams,
  registeredAtValueFormatter,
  storeNameTooltipValueGetter,
  subcategoryValueGetter,
} from 'src/utils/issue/issue-ag-grid-util';
import { buildIssuePanelModalData, IssuePanelModalData, issueCaseStatusBadgeMap } from 'src/utils/issue/issue-util';

import AgGridSettingsButtons from 'src/atomic-components/molecules/AgGridSettingsButtons/AgGridSettingsButtons';
import Loading from 'src/components/Loading/Loading';

import IssuePanelModal from '../IssuePanelModal';
import IssueLiabilityModal from './IssueLiabilityModal';
import { IssueManageMasterRowData } from './IssueManage.types';
import { detailCellRendererParams } from './IssueManageDetailGridConfig';

interface IssueManageTableProps {
  rowData: IssueManageMasterRowData[];
  isLoading: boolean;
}

export interface IssueManageTableHandle {
  expandAll: () => void;
  collapseAll: () => void;
}

const TABLE_ID = 'issueManageTable';

const defaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

const IssueManageTable = forwardRef<IssueManageTableHandle, IssueManageTableProps>(({ rowData, isLoading }, ref) => {
  const [gridRef, setGridRef] = useState<AgGridReact<IssueManageMasterRowData> | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<IssuePanelModalData>(null);

  // IssueLiability 편집 모달 상태
  const [liabilityModalOpen, setLiabilityModalOpen] = useState(false);
  const [liabilityModalCaseId, setLiabilityModalCaseId] = useState<string | null>(null);
  const [initialEditLiabilityId, setInitialEditLiabilityId] = useState<string | null>(null);

  // rowData에서 최신 데이터를 찾아서 사용 (실시간 업데이트 반영)
  const liabilityModalData = useMemo(() => {
    if (!liabilityModalCaseId) return null;
    return rowData.find((row) => row._id === liabilityModalCaseId) ?? null;
  }, [liabilityModalCaseId, rowData]);

  /**
   * 상세 버튼 클릭 핸들러
   * sourceType/sourceId를 기반으로 모달 props 결정
   */
  const handleOpenDetail = useCallback(
    (issueId: string, sourceType: IssueSourceType | null, sourceId: string | null) => {
      const data = buildIssuePanelModalData(issueId, sourceType, sourceId);
      setModalData(data);
      setModalOpen(true);
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setModalData(null);
  }, []);

  /**
   * 카테고리 + 태스크 개수 렌더러 (클릭 시 모달 열기)
   */
  const CategoryWithTaskCountAndModalRenderer = useMemo(
    () =>
      createCategoryWithTaskCountRenderer<IssueManageMasterRowData>({
        onRowClick: (data) => handleOpenDetail(data._id, data.sourceType ?? null, data.sourceId ?? null),
      }),
    [handleOpenDetail]
  );

  /**
   * 손실 책임 편집 모달 열기
   */
  const handleOpenLiabilityModal = useCallback((data: IssueManageMasterRowData, liabilityId?: string) => {
    setLiabilityModalCaseId(data._id);
    setInitialEditLiabilityId(liabilityId ?? null);
    setLiabilityModalOpen(true);
  }, []);

  /**
   * Tag 클릭 시 해당 liability 편집 모드로 모달 열기
   * - rowData 의존성 없이 ID만 저장하고, 실제 데이터는 liabilityModalData에서 조회
   */
  const handleLiabilityTagEdit = useCallback((issueCaseId: string, liabilityId: string) => {
    setLiabilityModalCaseId(issueCaseId);
    setInitialEditLiabilityId(liabilityId);
    setLiabilityModalOpen(true);
  }, []);

  const handleCloseLiabilityModal = useCallback(() => {
    setLiabilityModalOpen(false);
    setLiabilityModalCaseId(null);
    setInitialEditLiabilityId(null);
  }, []);

  /**
   * 외부에서 접근 가능한 메서드 노출
   */
  useImperativeHandle(
    ref,
    () => ({
      expandAll: () => {
        gridRef?.api?.forEachNode((node) => {
          if (node.master) {
            node.setExpanded(true);
          }
        });
      },
      collapseAll: () => {
        gridRef?.api?.forEachNode((node) => {
          if (node.master) {
            node.setExpanded(false);
          }
        });
      },
    }),
    [gridRef]
  );

  /**
   * 손실 관리 버튼 cellRenderer
   */
  const LiabilityEditButtonCellRenderer = useCallback(
    (params: ICellRendererParams<IssueManageMasterRowData>) => {
      const data = params.data;
      if (!data) return null;

      const count = data.issueLiabilities?.length ?? 0;

      return (
        <Button type='link' size='small' icon={<EditOutlined />} onClick={() => handleOpenLiabilityModal(data)}>
          {count > 0 ? `관리 (${count})` : '추가'}
        </Button>
      );
    },
    [handleOpenLiabilityModal]
  );

  const columnDefs = useMemo<(ColDef<IssueManageMasterRowData> | ColGroupDef<IssueManageMasterRowData>)[]>(() => {
    return [
      {
        headerName: '카테고리',
        children: [
          {
            field: '_id',
            headerName: 'ID',
            width: 100,
            hide: true,
          },
          {
            field: 'displayCategory',
            headerName: '요약',
            width: 200,
            pinned: 'left',
            columnGroupShow: 'closed',
            cellRenderer: 'agGroupCellRenderer',
            cellRendererParams: {
              innerRenderer: CategoryWithTaskCountAndModalRenderer,
            },
            cellStyle: categoryCellStyle,
          },
          {
            field: 'category',
            headerName: '분류',
            width: 160,
            pinned: 'left',
            columnGroupShow: 'open',
            cellRenderer: 'agGroupCellRenderer',
            cellRendererParams: {
              innerRenderer: CategoryWithTaskCountAndModalRenderer,
            },
            cellStyle: categoryCellStyle,
            filter: 'agSetColumnFilter',
            filterParams: {
              valueFormatter: categoryFilterValueFormatter,
              convertValuesToStrings: true,
            },
          },
          {
            field: 'subcategory',
            headerName: '세부분류',
            width: 100,
            pinned: 'left',
            columnGroupShow: 'open',
            valueGetter: subcategoryValueGetter,
          },
        ],
      },
      {
        headerName: '상태/일시',
        children: [
          {
            headerName: '요약',
            width: 160,
            columnGroupShow: 'closed',
            cellRenderer: createStatusDateSummaryCellRenderer<IssueManageMasterRowData>(issueCaseStatusBadgeMap),
            filter: false,
            sortable: false,
          },
          {
            field: 'status',
            headerName: '상태',
            width: 120,
            columnGroupShow: 'open',
            cellRenderer: createStatusCellRenderer<IssueManageMasterRowData>(issueCaseStatusBadgeMap),
            filterParams: {
              valueFormatter: issueCaseStatusValueFormatter,
            },
          },
          {
            field: 'registeredAt',
            headerName: '등록일시',
            width: 140,
            columnGroupShow: 'open',
            valueFormatter: registeredAtValueFormatter,
          },
        ],
      },
      {
        field: 'storeName',
        headerName: '매장명',
        width: 150,
        tooltipValueGetter: storeNameTooltipValueGetter,
      },
      {
        headerName: '이슈 상품',
        width: 200,
        cellRenderer: createProductsCellRenderer<IssueManageMasterRowData>(),
        filter: 'agSetColumnFilter',
        filterParams: productsFilterParams,
      },
      {
        headerName: '이미지',
        width: 100,
        cellRenderer: createImagesCellRenderer<IssueManageMasterRowData>(),
        filter: false,
        sortable: false,
      },
      createAmountColumnGroup(),
      {
        field: 'publicNote',
        headerName: '메모(공개)',
        width: 120,
      },
      {
        field: 'privateNote',
        headerName: '메모(비공개)',
        width: 120,
      },
      {
        headerName: '작성자',
        field: 'creatorName',
        width: 100,
        cellRenderer: createCreatorCellRenderer<IssueManageMasterRowData>(),
      },
      {
        field: 'issuedBy',
        headerName: '출처',
        width: 100,
        editable: true,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorPopup: true,
        cellEditorParams: issuedByCellEditorParams,
        valueFormatter: issuedByValueFormatter,
        onCellValueChanged: handleIssuedByCellValueChanged as ColDef['onCellValueChanged'],
      },
      {
        headerName: '손실 책임',
        width: 280,
        cellRenderer: createIssueLiabilitiesCellRenderer<IssueManageMasterRowData>(handleLiabilityTagEdit),
        autoHeight: true,
        filter: false,
        sortable: false,
      },
      {
        headerName: '손실 관리',
        width: 120,
        cellRenderer: LiabilityEditButtonCellRenderer,
        filter: false,
        sortable: false,
      },
    ];
  }, [CategoryWithTaskCountAndModalRenderer, LiabilityEditButtonCellRenderer, handleLiabilityTagEdit]);

  return (
    <>
      <div className='ag-theme-alpine height100'>
        <div className='tableButtons'>
          <AgGridSettingsButtons gridRef={gridRef} tableId={TABLE_ID} />
        </div>
        {isLoading && <Loading title='데이터를 불러오고 있습니다.' />}
        <AgGridReact<IssueManageMasterRowData>
          ref={setGridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data._id}
          isRowMaster={(dataItem) => (dataItem ? dataItem.issueTasks.length > 0 : false)}
          enableCellTextSelection={true}
          tooltipShowDelay={0}
          animateRows={true}
          onGridReady={() => loadGridState(gridRef, TABLE_ID)}
          // DetailRow
          keepDetailRows={true}
          masterDetail={true}
          detailRowAutoHeight={true}
          detailCellRendererParams={detailCellRendererParams}
        />
      </div>
      {modalData && <IssuePanelModal open={modalOpen} onClose={handleCloseModal} {...modalData} />}
      {liabilityModalData && (
        <IssueLiabilityModal
          open={liabilityModalOpen}
          onClose={handleCloseLiabilityModal}
          issueCase={liabilityModalData}
          initialEditLiabilityId={initialEditLiabilityId}
        />
      )}
    </>
  );
});

IssueManageTable.displayName = 'IssueManageTable';

export default IssueManageTable;
